<script setup lang="ts">
  import { ToastContentProps, ToastContentType } from '@/shared/lib';
  import ToastSuccessIcon from './ToastSuccessIcon.vue';
  import ToastErrorIcon from './ToastErrorIcon.vue';
  import ToastWarningIcon from './ToastWarningIcon.vue';
  import ToastInfoIcon from './ToastInfoIcon.vue';

  const props = defineProps<ToastContentProps>();

  const emit = defineEmits<{ closeToast: [] }>();

  function handleUndo() {
    props.onUndo?.();
    emit('closeToast');
  }

  function handleDismissOnly(event: MouseEvent) {
    event.stopPropagation();
    emit('closeToast');
  }

  const handleTextColor = (type: ToastContentType) => {
    if (type === 'success') {
      return 'text-gray-05';
    }
    return 'text-gray-07';
  };
</script>

<template>
  <div class="flex flex-col gap-y-2 relative w-full min-w-0">
    <button
      v-if="!props.onUndo"
      class="absolute right-2 top-1/2 z-10 w-5 h-5 -translate-y-1/2 flex items-center justify-center text-gray-07 hover:text-gray-01 opacity-70 hover:opacity-100 transition-opacity duration-200 bg-transparent border-none cursor-pointer text-lg leading-none"
      aria-label="Fechar notificação"
      @click="emit('closeToast')"
    >
      ×
    </button>

    <div
      class="flex items-center gap-x-3 min-w-0 w-full"
      :class="props.onUndo ? 'pr-1' : 'pr-10'"
    >
      <component
        :is="
          {
            success: ToastSuccessIcon,
            error: ToastErrorIcon,
            warning: ToastWarningIcon,
            info: ToastInfoIcon,
          }[props.type]
        "
        v-if="!props.hideIcon"
      />
      <h3 class="text-sm text-gray-07 flex-1 min-w-0">
        <strong>{{ props.title }}</strong>
      </h3>
      <div
        v-if="props.onUndo"
        class="flex items-center gap-3 shrink-0"
      >
        <button
          type="button"
          class="text-sm font-bold text-toast-success-icon hover:underline bg-transparent border-none cursor-pointer p-0 leading-none"
          @click="handleUndo"
        >
          {{ props.undoLabel ?? 'Desfazer' }}
        </button>
        <button
          type="button"
          class="w-5 h-5 flex items-center justify-center text-gray-07 hover:text-gray-01 opacity-70 hover:opacity-100 transition-opacity duration-200 bg-transparent border-none cursor-pointer text-lg leading-none p-0 shrink-0"
          aria-label="Fechar notificação"
          @click="handleDismissOnly"
        >
          ×
        </button>
      </div>
    </div>
    <p
      v-if="props.message"
      class="text-sm !pl-8.5"
      :class="[handleTextColor(props.type), props.onUndo ? 'pr-1' : 'pr-10']"
    >
      {{ props.message }}
    </p>

    <div
      v-if="props.validationErrors"
      class="!pl-8.5"
      :class="props.onUndo ? 'pr-1' : 'pr-10'"
    >
      <ul
        class="text-sm space-y-1"
        :class="handleTextColor(props.type)"
      >
        <li
          v-for="(errors, field) in props.validationErrors"
          :key="field"
        >
          <span
            v-for="(error, index) in errors"
            :key="index"
            class="block"
          >
            • <strong>{{ field }}:</strong> {{ error }}
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>
